import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NAVIGATION_LINKS, COMPANY_INFO } from '../constants';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { BrandLogo } from './BrandLogo';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (href === '#') {
      if (location.pathname !== '/') {
        navigate('/');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
           const el = document.querySelector(href);
           if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
       navigate(href);
    }
  };

  const isHeroHeader = !isScrolled && location.pathname === '/';

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header 
        className={`fixed w-full z-50 transition-all duration-500 ${
          isScrolled 
            ? 'glass-nav py-3' 
            : 'bg-transparent py-6'
        }`}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <a 
              href="#" 
              className="flex items-center group focus:outline-none focus:ring-2 focus:ring-accent rounded-2xl p-1 max-[360px]:p-0" 
              onClick={(e) => handleNavClick(e, '#')}
              aria-label="A2 Innovations Home"
            >
              <BrandLogo tone={isHeroHeader ? 'on-dark' : 'on-light'} />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-2" role="navigation" aria-label="Main Navigation">
              {NAVIGATION_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent ${
                    isScrolled || location.pathname !== '/' 
                      ? 'text-slate-600 hover:text-accent hover:bg-slate-100' 
                      : 'text-slate-300 hover:text-white hover:bg-white/10'
                  }`}
                  onClick={(e) => handleNavClick(e, link.href)}
                >
                  {link.label}
                </a>
              ))}
              <div className="pl-4">
                <a 
                  href="#contact" 
                  onClick={(e) => handleNavClick(e, '#contact')}
                  className={`px-6 py-2.5 rounded-2xl text-sm font-semibold transition-all duration-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 hover:-translate-y-1 active:scale-[0.98] ${
                    isScrolled || location.pathname !== '/' 
                      ? 'bg-slate-900 text-white hover:bg-accent hover:shadow-2xl hover:shadow-accent/40' 
                      : 'bg-white text-slate-900 hover:bg-slate-100 hover:shadow-2xl hover:shadow-slate-200/50'
                  }`}
                >
                  Get Started
                </a>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className={`md:hidden p-2 max-[360px]:p-1.5 rounded-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent ${isScrolled || location.pathname !== '/' ? 'hover:bg-slate-100 text-slate-900' : 'hover:bg-white/10 text-white'}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen ? 'true' : 'false'}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
            </button>
          </div>
        </div>

        {/* Full Screen Mobile Nav Overlay */}
        <div 
          id="mobile-menu"
          className={`fixed inset-0 bg-slate-900/95 backdrop-blur-xl z-40 transition-all duration-500 flex flex-col justify-center items-center ${
            isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile Navigation"
        >
             <div className="flex flex-col space-y-6 text-center">
             {NAVIGATION_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-2xl font-bold text-white hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded-lg px-4 py-2"
                  onClick={(e) => handleNavClick(e, link.href)}
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-8">
                <a 
                  href="#contact" 
                  onClick={(e) => handleNavClick(e, '#contact')}
                  className="inline-block px-8 py-4 bg-accent text-white rounded-2xl text-lg font-semibold shadow-glow focus:outline-none focus:ring-2 focus:ring-white hover:-translate-y-1 active:scale-[0.98] transition-all duration-300"
                >
                  Get Started
                </a>
              </div>
             </div>
             <button 
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute top-8 right-8 text-white/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-accent rounded-full p-2"
                aria-label="Close menu"
             >
               <X className="w-8 h-8" aria-hidden="true" />
             </button>
        </div>
      </header>

      <main className="flex-grow" role="main">
        {children}
      </main>

      <footer className="bg-slate-950 text-slate-400 py-20 border-t border-slate-900/50" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="col-span-1 md:col-span-2">
              <div className="mb-6">
                <BrandLogo tone="footer" />
              </div>
              <p className="mb-8 max-w-sm text-slate-400 leading-relaxed text-base">
                Empowering organizations through world-class Workday implementation and support services. We build the future of workforce management.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-slate-900 rounded-2xl flex items-center justify-center hover:bg-accent hover:text-white hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 border border-slate-800 focus:outline-none focus:ring-2 focus:ring-accent">
                   <span className="sr-only">LinkedIn</span>
                   <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
                <a href="#" className="w-10 h-10 bg-slate-900 rounded-2xl flex items-center justify-center hover:bg-accent hover:text-white hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 border border-slate-800 focus:outline-none focus:ring-2 focus:ring-accent">
                   <span className="sr-only">Twitter</span>
                   <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-6">Expertise</h4>
              <ul className="space-y-4">
                <li><a href="#" onClick={(e) => handleNavClick(e, '#services')} className="flex items-center hover:text-accent transition-colors group focus:outline-none focus:ring-2 focus:ring-accent rounded-lg -ml-2 px-2 py-1"><ArrowUpRight className="w-4 h-4 mr-2 text-slate-600 group-hover:text-accent transition-colors" aria-hidden="true" />Implementation</a></li>
                <li><a href="#" onClick={(e) => handleNavClick(e, '#services')} className="flex items-center hover:text-accent transition-colors group focus:outline-none focus:ring-2 focus:ring-accent rounded-lg -ml-2 px-2 py-1"><ArrowUpRight className="w-4 h-4 mr-2 text-slate-600 group-hover:text-accent transition-colors" aria-hidden="true" />Managed Services</a></li>
                <li><a href="#" onClick={(e) => handleNavClick(e, '#services')} className="flex items-center hover:text-accent transition-colors group focus:outline-none focus:ring-2 focus:ring-accent rounded-lg -ml-2 px-2 py-1"><ArrowUpRight className="w-4 h-4 mr-2 text-slate-600 group-hover:text-accent transition-colors" aria-hidden="true" />Payroll Optimization</a></li>
                <li><a href="#" onClick={(e) => handleNavClick(e, '#services')} className="flex items-center hover:text-accent transition-colors group focus:outline-none focus:ring-2 focus:ring-accent rounded-lg -ml-2 px-2 py-1"><ArrowUpRight className="w-4 h-4 mr-2 text-slate-600 group-hover:text-accent transition-colors" aria-hidden="true" />Integrations</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-6">Contact</h4>
              <ul className="space-y-4 text-base leading-relaxed">
                <li className="flex items-start">
                  <span className="text-slate-500 w-20 flex-shrink-0">Address:</span>
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(COMPANY_INFO.fullAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded-lg px-1 -mx-1"
                  >
                    {COMPANY_INFO.address}, {COMPANY_INFO.suite}<br />{COMPANY_INFO.city}
                  </a>
                </li>
                <li className="flex items-center">
                   <span className="text-slate-500 w-20 flex-shrink-0">Email:</span>
                   <a href="mailto:a2innovations@gmail.com" className="text-slate-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded-lg px-1 -mx-1">
                     a2innovations@gmail.com
                   </a>
                </li>
                <li className="flex items-center">
                   <span className="text-slate-500 w-20 flex-shrink-0">Phone:</span>
                   <a href="tel:+16472108270" className="text-slate-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded-lg px-1 -mx-1">
                     +1 (647) 210-8270
                   </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-slate-900 flex flex-col md:flex-row justify-between items-center text-sm text-slate-600">
            <div>&copy; {new Date().getFullYear()} {COMPANY_INFO.name.replace(/\.$/, '')}. All rights reserved.</div>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded px-1">Privacy Policy</a>
              <a href="#" className="hover:text-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded px-1">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};